<template>
  <component :is="tag" ref="root" v-bind="htmlAttrs">
    <slot />
  </component>
</template>

<script>
// @ts-nocheck
import Sortable from 'sortablejs';

const SORTABLE_EVENTS = [
  'Start',
  'Add',
  'Remove',
  'Update',
  'End',
  'Choose',
  'Unchoose',
  'Sort',
  'Filter',
  'Clone',
  'Move',
];

const READ_ONLY_OPTIONS = SORTABLE_EVENTS.map((event) => `on${event}`);

function camelize(value) {
  return value.replace(/-(\w)/g, (_, char) => char.toUpperCase());
}

function isHtmlAttribute(key) {
  return (
    key === 'id' ||
    key === 'class' ||
    key === 'style' ||
    key.startsWith('data-') ||
    key.startsWith('aria-')
  );
}

function getSortableOptions(attrs) {
  return Object.entries(attrs)
    .filter(([key]) => !isHtmlAttribute(key))
    .map(([key, value]) => [camelize(key), value])
    .filter(([key]) => !READ_ONLY_OPTIONS.includes(key))
    .reduce((options, [key, value]) => {
      options[key] = value;
      return options;
    }, {});
}

export default {
  name: 'OvDraggable',
  inheritAttrs: false,
  props: {
    tag: {
      type: String,
      default: 'div',
    },
    value: {
      type: Array,
      default: null,
    },
    modelValue: {
      type: Array,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'input',
    'update:modelValue',
    'change',
    ...SORTABLE_EVENTS.map((event) => event.toLowerCase()),
  ],
  computed: {
    htmlAttrs() {
      return Object.entries(this.$attrs).reduce((attrs, [key, value]) => {
        if (isHtmlAttribute(key)) {
          attrs[key] = value;
        }
        return attrs;
      }, {});
    },
    list() {
      if (this.modelValue != null) {
        return this.modelValue;
      }
      return this.value;
    },
  },
  watch: {
    disabled(value) {
      if (this._sortable) {
        this._sortable.option('disabled', value);
      }
    },
  },
  mounted() {
    this.initSortable();
  },
  beforeUnmount() {
    if (this._sortable) {
      this._sortable.destroy();
      this._sortable = null;
    }
  },
  methods: {
    initSortable() {
      const root = this.$refs.root;
      if (!root) {
        return;
      }

      const target = root.nodeType === 1 ? root : root.parentElement;
      const options = getSortableOptions(this.$attrs);
      const eventHandlers = {};

      SORTABLE_EVENTS.forEach((event) => {
        const eventName = event.toLowerCase();
        eventHandlers[`on${event}`] = (evt) => {
          if (event === 'End') {
            this.onDragEnd(evt);
          }
          this.$emit(eventName, evt);
        };
      });

      this._sortable = Sortable.create(target, {
        ...options,
        ...eventHandlers,
        disabled: this.disabled,
      });
    },

    onDragEnd(evt) {
      if (evt.oldIndex == null || evt.newIndex == null || evt.oldIndex === evt.newIndex) {
        return;
      }

      const list = this.list;
      if (!Array.isArray(list)) {
        return;
      }

      const newList = list.slice();
      const [element] = newList.splice(evt.oldIndex, 1);
      newList.splice(evt.newIndex, 0, element);
      const moved = {
        element,
        oldIndex: evt.oldIndex,
        newIndex: evt.newIndex,
      };

      // Always emit. Vue 2 compat stores the v-model listener as onModelCompat:input,
      // which the old $attrs check never saw, so the parent list stayed in the old order.
      this.$emit('update:modelValue', newList);
      this.$emit('input', newList);
      this.$emit('change', { moved });
    },
  },
};
</script>
